<template>
  <v-container @keydown.enter.stop.prevent @keyup.enter.stop.prevent>
    <!-- Header Section with Penalty Date, Print/Email Toggle, and Filters -->
    <v-row>
      <v-col cols="4" class="pt-0 pb-0 d-flex justify-space-between">
        <ClarionDateControl
          class="input-text-size"
          v-model="editedItem.curPenaltyDate"
          @input="penaltyCaclFunc"
          label="Penalty Date"
          :isToggle="false"
        />
        <BinaryToggle
          id="pickListPrintOrEmailToggle"
          aria-label="Statements: Print or Email"
          compact
          off-label="Print"
          on-label="Email"
          v-model="togglePrintOrEmail" />
      </v-col>

      <v-col cols="8" class="pt-0 pb-0">
        <v-row>
          <v-col cols="1" class="pt-0 pb-0"></v-col>
          <v-col cols="3" class="pt-0 pb-0">
            <CommonDropDowns
              :isItTaxYear="true"
              @change="dataFromCommonDropDowns"
              :taxYearValue="criteriaState.taxYear"
            />
          </v-col>
          <v-col cols="4" class="pt-0 pb-0">
            <CommonDropDowns
              :isItMtgCode="true"
              @change="dataFromCommonDropDowns"
              :mtgCodeValue="criteriaState.mtgCode"
            />
          </v-col>
          <v-col cols="4" class="pt-0 pb-0">
            <CommonDropDowns
              :isItstaCode="true"
              @change="dataFromCommonDropDowns"
              :staCodeValue="criteriaState.statusCode"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Profile Management Row -->
    <v-row class="pa-0">
      <v-col cols="3" class="mt-n7">
        <v-combobox
          class="input-text-size"
          label="Profile Name"
          :items="pickListProfileList"
          v-model="criteriaState.profileName"
          @input.native="criteriaState.profileName=$event.srcElement.value"
          @change="searchByCriteria"
        />
      </v-col>

      <v-col cols="1" class="mt-n3">
        <v-btn
          class="input-text-size"
          text
          color="error darken-1"
          @click="onPickListDelete('fromDeleteButton')"
        >
          Delete
        </v-btn>
      </v-col>
      <v-col cols="1" class="mt-n3">
        <v-btn
          class="input-text-size"
          text
          color="blue darken-1"
          @click="onPickListSave()"
        >
          Save
        </v-btn>
      </v-col>
      <v-col cols="1" class="mt-n3">
        <v-btn
          class="input-text-size"
          text
          color="blue darken-1"
          @click="addNewProfileName()"
        >
          Add New
        </v-btn>
      </v-col>
      <v-col cols="3" v-if="paymentSettingItem.preSortEnvelopes">
        <v-text-field dense class="input-text-size ml-5"
          hide-details label="Envelope#"
          v-model.number="criteriaState.envelopeNumber">
        </v-text-field>
      </v-col>
      <v-col cols="3">
        <v-text-field
          ref="barcodeInput"
          dense
          class="input-text-size ml-5"
          hide-details
          label="Scan Barcode"
          v-model="barcodeInput"
          @input="handlePickListBarCodeScan"
          clearable
        />
      </v-col>
      <v-col cols="1" class="mt-n3 d-flex align-center">
        <BinaryToggle
          id="pickListHalfPaymentToggle"
          aria-label="Payment amount: Full or Half"
          compact
          off-label="Full"
          on-label="Half"
          v-model="criteriaState.halfPayment" />
      </v-col>
      <v-col cols="2" class="mt-n3 d-flex align-center">
        <v-btn
          class="input-text-size"
          text
          color="blue darken-1"
          @click="loadRecordsClicked()"
          :disabled="loadRecordsDisable"
        >
          Load Records
        </v-btn>
      </v-col>
    </v-row>

    <!-- Pick List Table -->
    <PickListTable
      ref="pickListTableRef"
      :selectedPenaltyDate="editedItem.curPenaltyDate"
      @refetchTaxInquiryData="refetchTaxInquiryData"
    />

    <!-- Options and Settings Section -->
    <v-row>
      <v-col cols="2">
        <v-tooltip bottom :disabled="hasOriginalApportionment">
          <template v-slot:activator="{ on, attrs }">
            <div v-if="showExempt" v-bind="attrs" v-on="on">
              <v-checkbox
                class="input-text-size"
                v-model="enableExempt"
                :disabled="!hasOriginalApportionment"
                label="Enable exempt"
              />
            </div>
          </template>
          <span>No "Original" exempt apportionment records exist for the selected tax year</span>
        </v-tooltip>
      </v-col>

      <v-col cols="3" class="mt-2" v-if="enableExempt">
        <v-row align="center">
          <v-text-field
            class="input-text-size"
            label="Amount for exempt"
            v-model.number="exemptAmount"
          />
          <v-btn
            small
            text
            color="blue"
            :disabled="!enableExempt"
            @click="handleTaxAmountBreakdown"
          >
            Apply
          </v-btn>
        </v-row>
      </v-col>

      <v-col cols="1" class="mt-2" v-if="enableExempt"></v-col>
      <v-col cols="4" class="mt-2" v-if="!enableExempt"></v-col>

      <v-col cols="3">
        <v-checkbox
          class="input-text-size"
          v-model="fullLegalDescription"
          label="Full legal description"
        />
      </v-col>

      <v-col cols="3">
        <v-checkbox
          class="input-text-size"
          v-model="singleLineInfo"
          label="Single Line info List"
        />
      </v-col>
    </v-row>

    <!-- Additional Options Row -->
    <v-row class="mt-n15 mb-n10">
      <v-col cols="2"></v-col>
      <v-col cols="3" class="mt-2" v-if="enableExempt"></v-col>
      <v-col cols="1" class="mt-2" v-if="enableExempt"></v-col>
      <v-col cols="4" class="mt-2" v-if="!enableExempt"></v-col>

      <v-col cols="3">
        <v-checkbox
          class="input-text-size"
          v-model="addEmailAddressOnly"
          label="Add Email or Phone #"
        />
      </v-col>
    </v-row>
    <!-- Action Buttons Section -->
    <v-row>
      <v-col cols="12" class="d-flex justify-start mb-0 btns-ctnr overflow-x-auto">
        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          class="input-text-size tax-common-action-btn-email-statement"
          @click="handleMakePayment"
        >
          Pay Now
        </v-btn>

        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          class="input-text-size tax-common-action-btn-email-statement"
          @click="handleSMSDialog"
        >
          SMS
        </v-btn>

        <v-btn
          v-if="togglePrintOrEmail"
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          class="input-text-size tax-common-action-btn-email-statement"
          @click="handleEmailStatement"
        >
          Statement
        </v-btn>
        <v-btn
          v-else
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="printStatement"
        >
          Print Statement
        </v-btn>

        <v-btn
          v-if="!togglePrintOrEmail"
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="printList"
        >
          Info List
        </v-btn>
        <v-btn
          v-else
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="handleEmailInfoList"
        >
          Info List
        </v-btn>

        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="handleAddComments"
        >
          Add Comment
        </v-btn>

        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="handleMtgcode"
        >
          Apply MTG Code
        </v-btn>

        <v-tooltip bottom :disabled="!taxSettingItem.disableAddressChangeOnPickList">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" style="display: inline-block">
              <v-btn
                small
                text
                color="blue"
                :disabled="isAddressChangeDisabled"
                @click="handleAddressChange"
              >
                {{ addEmailAddressOnly ? 'Change Email/Phone' : 'Change Address' }}
              </v-btn>
            </div>
          </template>
          <span>Disabled in Tax Settings</span>
        </v-tooltip>

        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          @click="printAddressLabel"
        >
          Print Label
        </v-btn>

        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl"
          v-on:click.right="onDeleteRightClick"
          @contextmenu.prevent
          @click="onDeleteLeftClick"
        >
          Delete
        </v-btn>

        <!-- profile name is selected, then disable the empty list button -->
        <v-btn
          small
          text
          color="blue"
          :disabled="!isPickListItemsAvl || !!criteriaState.profileName"
          @click="emptyListHandler"
        >
          Empty
        </v-btn>
        <UploadFile
          bucketName="taxInquiry"
          program="taxInquiry"
          parentObjectType="taxInquiryTransaction"
          :parentObjectId="pickListGroupId"
          :itemIsSaved="true"
        >
          <template v-slot:activator="{ activatorProps }">
            <v-btn
              small
              text
              color="blue"
              :disabled="!isPickListItemsAvl"
              @click="activatorProps.leftClick"
              @contextmenu.prevent="activatorProps.rightClick"
            >
              {{ activatorProps.caption }}
            </v-btn>
          </template>
        </UploadFile>
      </v-col>
    </v-row>
    <!-- Confirmation Popup -->
    <CommonPopUp
      v-model="emptyPickListFlagValue"
      :enableFlashTypeMsg="false"
      :custumWidth="300"
    >
      <template v-slot:customsLabelOrInfo>
        <p style="white-space: pre;">{{ messageText }}</p>
      </template>

      <template v-slot:[`customsButtonsForPopUp`]="{}">
        <v-btn
          class="input-text-size"
          text
          color="blue darken-1"
          @click="openConfirmPopUp('yes')"
        >
          Yes
        </v-btn>
        <v-btn
          class="input-text-size"
          text
          color="blue darken-1"
          @click="openConfirmPopUp('no')"
        >
          No
        </v-btn>
      </template>
    </CommonPopUp>

    <!-- Email Statement Dialog for Pick List -->
    <CRUDDialog
      :baseCrudKey="emailStatementCrudKeyPickList"
      :canSave="true"
      :onlyDialogHeaderName="emailStatementHeader"
      dialogDynamicWidth="550"
      canSaveCustomLabel="Send"
      @upsertItem="handleEmail"
    >
      <template v-slot:[`editedItem`]="{}">
        <EmailStatementDialoge :baseCrudKey="emailStatementCrudKeyPickList" />
      </template>
    </CRUDDialog>

    <!-- Email Statements Dialog -->
    <CRUDDialog
      :baseCrudKey="emailStatements"
      :canSave="true"
      :onlyDialogHeaderName="emailStatementHeader"
      dialogDynamicWidth="550"
      canSaveCustomLabel="Send"
      @upsertItem="handleEmailStatements"
    >
      <template v-slot:[`editedItem`]="{}">
        <EmailStatementDialoge :baseCrudKey="emailStatements" />
      </template>
    </CRUDDialog>

    <!-- SMS: cart link includes all pick list tax year / tax id pairs -->
    <CRUDDialog
      :baseCrudKey="smsCrudKey"
      :canSave="true"
      :onlyDialogHeaderName="smsDialogtHeader"
      dialogDynamicWidth="550"
      canSaveCustomLabel="Send"
      @upsertItem="generateSMS"
    >
      <template v-slot:[`editedItem`]="{}">
        <SmsDialog :baseCrudKey="smsCrudKey" />
      </template>
    </CRUDDialog>

    <!-- MTG Code Dialog -->
    <CRUDDialog
      baseCrudKey="picklistmtgcode"
      :canSave="true"
      dialogHeaderName="Mtg Code to Picklist"
      dialogDynamicWidth="400"
      @upsertItem="handleMtgCodeUpsert"
    >
      <template v-slot:[`editedItem`]="{}">
        <div>
          <v-row>
            <v-col cols="6" class="pt-7 pb-0">
              <label
                for="mtg code"
                style="font-weight: bold; margin-left: 1rem;"
              >
                Select Mtg Code:
              </label>
            </v-col>

            <v-col cols="6" class="pt-2 pb-0">
              <CommonDropDowns
                :isItMtgCode="true"
                @change="dataFromCommonDropDowns"
                :mtgCodeValue="criteriaState.mtgCode"
              />
            </v-col>
            <v-col cols="12">
              <div style="display: flex; justify-content: center;">
                <span style="font-weight: bold; margin-left: 1rem; margin-right: 1rem;">
                  Please select a Mortgage Code from the options above.
                </span>
              </div>
            </v-col>
          </v-row>
        </div>
      </template>
    </CRUDDialog>
    <v-dialog v-model="showMailingDialog" max-width="380" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Statement Mailing</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          Are you mailing this statement?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmStatementMailed(false)">No</v-btn>
          <v-btn text color="primary" @click="confirmStatementMailed(true)">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import { clients } from '../../../../util/clients';
import { getRawJwt } from '../../../../util/jwt';
import {
  getBusinessDateForPaymentEntry, toFiscalYear, toIsoDate, toShortDate,
} from '../../../../util/shared/tmc-global';
import { amountFormat, amountToNumber } from '../../../../util/shared/vue-global';
import { getWorkstationNumber } from '../../../../util/workstation-util';
import BinaryToggle from '../../../common/base/BinaryToggle.vue';
import ClarionDateControl from '../../../common/ClarionDateControl.vue';
import CommonDropDowns from '../../common-utils/CommonDropDowns.mixin.vue';
import { EXEMPT_STATUS_CONSTANTS, SEARCH_DEBOUNCE_TIMER } from '../../common-utils/constants';
import { exemptManufacturingStatusCode, penaltyCalculation, taxCommentTypesObj } from '../../common-utils/tax-common.util';
import PickListTable from './PickListTable.vue';
// import { validateQueryString } from '../../../../views/OD/report-util';
import '../../../../assets/style.css';
import { printerHandler } from '../../../../util/pdf-to-printer.util';
import { middleSectionParam, middleSectionValue } from '../../../../util/statement-middle-section.util';
import CRUDDialog from '../../../common/base/crud-2.0/CRUDDialog.vue';
import { generateQueryString } from '../../../common/base/report/report-util';
import CommonPopUp from '../../common-utils/CommonPopUp.vue';
import { penaltyCaclCrudListCommonFunc, taxFeeCheckWitheEfectiveDate } from '../../common-utils/CommonTaxFunctionalities';
import EmailStatementDialoge from './EmailStatementDialoge.vue';
import SmsDialog from './smsDialog.vue';
import taxSmsDialogMixin from '../../common-utils/taxSmsDialog.mixin';
import buildTaxSmsCartLinkUrl, { resolveTaxSmsUrlParts } from '../../common-utils/taxSmsCartLink.util';
import UploadFile from '../../../common/UploadFile.vue';

const { backendRest } = clients.direct;

export default {
  name: 'PickListTab',
  mixins: [taxSmsDialogMixin],
  data() {
    return {
      smsGenerateOpensUrlOnly: false,
      checkbox: true,
      singleLineInfo: false,
      addEmailAddressOnly: false,
      fullLegalDescription: false,
      pickListBaseCrudKey: 'pickListTable',
      pickListDialogBaseCrudKey: 'pickListMakePaymentDialog',
      pickListCriteriaBaseCrudKey: 'pickListCriteria',
      baseCrudKeyPickListFeeList: 'pickListFeeList',
      clickedButton: null,
      delayTimerId: null,
      showMailingDialog: false,
      pendingStatementUrl: null,
      exemptAmount: '',
      enableExempt: false,
      hasOriginalApportionment: false,
      togglePrintOrEmail: false,
      halfPayment: false,
      loadRecordsDisable: false,
      messageText: 'Empty pick list?',
      wkstNumber: getWorkstationNumber(),
      emailStatementHeader: 'Email Send',
      emailStatementCrudKeyPickList: 'emailStatementPickList',
      // Must stay distinct from the key CommonActionButtons uses for its own
      // Email Send dialog ('emailStatementsCrudKey'). Both components are mounted
      // at the same time on Tax Roll / Owner Search, so a shared key opens two
      // identical v-dialogs at once; on the second open they tie on z-index and
      // their retain-focus traps fight, leaving the dialog impossible to type in.
      emailStatements: 'pickListEmailStatementsCrudKey',
      smsCrudKey: 'smsPickList',
      smsDialogtHeader: 'SMS Info',
      barcodeInput: null,
      lastScannedBarcode: null,
      barcodeScanTimer: null,
    };
  },
  props: {
    baseCrudKey: {
      type: String,
      default: '',
    },
  },
  components: {
    BinaryToggle,
    ClarionDateControl,
    PickListTable,
    CommonPopUp,
    CommonDropDowns,
    CRUDDialog,
    EmailStatementDialoge,
    SmsDialog,
    UploadFile,
  },
  computed: {
    ...mapGetters('base/crud', ['item', 'items', 'isVisible', 'criteria', 'highlightedId']),
    ...mapGetters([
      'todaysDate',
    ]),
    ...mapState({
      nextPaymentNumber: (state) => state.tax.taxPayment.nextPaymentNumber,
      emptyPickListFlag: (state) => state.tax.taxInquiryTabs.emptyPickListFlag,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem,
      statementSettingItem: (state) => state.SystemConfig.statementSettingItem || {},
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem,
    }),
    itemsGetter() {
      const itemData = this.item(this.pickListBaseCrudKey);
      return itemData;
    },
    pickListItemsGetter() {
      const itemData = this.items(this.pickListBaseCrudKey);
      return itemData;
    },
    pickListProfileList() {
      const itemData = this.items('pickListProfileNames');
      return itemData || [];
    },
    isPickListItemsAvl() {
      return this.pickListItemsGetter.length > 0;
    },
    isAddressChangeDisabled() {
      return !this.isPickListItemsAvl || this.taxSettingItem.disableAddressChangeOnPickList;
    },
    taxInquiryData() {
      const taxInquiryItems = this.items('ownerTransactionList');
      return taxInquiryItems;
    },
    editedItem: {
      get() {
        return this.item('pickListMakePaymentDate');
      },
      set(value) {
        this.setItem(['pickListMakePaymentDate', value]);
      },
    },
    isVisibleProp() {
      const visibleFlag = this.isVisible(this.baseCrudKey);
      if (visibleFlag === undefined) {
        return false;
      }
      return visibleFlag;
    },
    criteriaState: {
      get() {
        return this.criteria(this.pickListCriteriaBaseCrudKey);
      },
      set(value) {
        this.setCriteria([this.pickListCriteriaBaseCrudKey, value]);
      },
    },
    emptyPickListFlagValue: {
      get() {
        return this.emptyPickListFlag;
      },
      set(value) {
        this.setEmptyPickListFlag(value);
      },
    },
    editEmailItem: {
      get() {
        return this.item(this.emailStatementCrudKeyPickList);
      },
      set(value) {
        this.setItem([this.emailStatementCrudKeyPickList, value]);
      },
    },
    editEmailItemStatements: {
      get() {
        return this.item(this.emailStatements);
      },
      set(value) {
        this.setItem([this.emailStatements, value]);
      },
    },
    editSMSItem: {
      get() {
        return this.item(this.smsCrudKey);
      },
      set(value) {
        this.setItem([this.smsCrudKey, value]);
      },
    },
    showExempt() {
      const items = this.items(this.pickListBaseCrudKey);
      if (this.criteriaState.statusCode === EXEMPT_STATUS_CONSTANTS && items.length > 0) {
        return true;
      }
      return false;
    },
    pickListGroupId() {
      const items = this.pickListItemsGetter;
      if (items && items.length > 0) {
        const ids = items
          .filter((r) => r.taxInquiryTransaction)
          .map((r) => String(r.taxInquiryTransaction));
        if (ids.length > 0) return ids.join(',');
      }
      return 'n/a';
    },
  },
  watch: {
    // watching when the dialog is opened, to reset some fields
    isVisibleProp(newVal) {
      if (newVal) this.resetDefault();
    },
    // Watch for Full/Half toggle changes
    'criteriaState.halfPayment': {
      handler() {
        // Call getPickLists from PickListTable component when toggle changes
        if (this.$refs.pickListTableRef) {
          this.$refs.pickListTableRef.getPickLists();
        }
      },
    },
    // the exempt checkbox is only shown while status code 406 is loaded; check as soon as it appears
    showExempt(newVal) {
      if (newVal) this.checkOriginalApportionmentExists();
      else this.hasOriginalApportionment = false;
    },
    // re-check if the tax year changes while the exempt checkbox is showing
    'criteriaState.taxYear': {
      handler() {
        if (this.showExempt) this.checkOriginalApportionmentExists();
      },
    },
    // users must not be able to keep exempt enabled once the backing records disappear
    hasOriginalApportionment(newVal) {
      if (!newVal) this.enableExempt = false;
    },
    // keep the "Attach Files" count current as rows are added/removed from the pick list
    pickListGroupId: {
      handler() {
        this.loadPickListAttachedFiles();
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setIsVisible',
      'setItem',
      'setItems',
      'setCriteria',
      'setHighlightedId',
      'setIndex',
    ]),
    ...mapActions('tax/taxPayment', [
      'generateMultipleReceipt',
    ]),
    ...mapActions('tax/taxPayment', [
      'loadPayment',
    ]),
    ...mapActions('tax/taxInquiryTabs', [
      'pickListLoadAndBuildRecords',
      'loadPickLists',
      'upsertPickListProfileNames',
      'deletePickListBasedOnProfileName',
      'deletePickList',
      'deleteAllPickList',
      'updateMtgToPickList',
      'UpsertBrowsePaidByPickListSingleNMultiplipleHybrid',
    ]),
    ...mapActions('tax/trWorkFile', [
      'sendMailTR',
    ]),
    ...mapActions('tax/taxInquires', [
      'sendMail',
    ]),
    ...mapMutations('tax/taxInquiryTabs', [
      'setEmptyPickListFlag',
    ]),
    ...mapActions(['reAuth', 'custumColrFlashMsg']),
    ...mapActions('tax/taxInquires', ['loadPickListResults']),
    ...mapActions('tax/taxExemptMfg', ['hasOriginalExemptApportionment']),
    async checkOriginalApportionmentExists() {
      if (!this.criteriaState.taxYear) {
        this.hasOriginalApportionment = false;
        return;
      }
      this.hasOriginalApportionment = await this.hasOriginalExemptApportionment({
        criteria: { taxYear: this.criteriaState.taxYear },
      });
    },
    handleAddComments() {
      // when its created from pick list it will be 7
      this.setItem(['pickListAddCommentsDialog', { category: taxCommentTypesObj.userCommentCode }]);
      this.setIsVisible(['pickListAddCommentsDialog', true]);
    },
    handleMtgcode() {
      // this.setItem(['picklistmtgcode', { mtgCode: '' }]);
      this.setIsVisible(['picklistmtgcode', true]);
    },
    ...mapActions('tax/taxFee', [
      'taxFeesBasedOnIds',
    ]),
    ...mapActions('SystemConfig', [
      'getGlobalSettingStore',
    ]),
    ...mapActions('files', [
      'loadAttachedFiles',
    ]),
    loadPickListAttachedFiles() {
      this.loadAttachedFiles({
        bucketName: 'taxInquiry',
        program: 'taxInquiry',
        parentObjectType: 'taxInquiryTransaction',
        parentObjectId: this.pickListGroupId,
      });
    },
    async onDeleteRightClick() {
      const item = {
        workstation: Number(this.wkstNumber),
      };
      await this.deleteAllPickList({ item, baseCrudKey: this.pickListBaseCrudKey });
      this.$refs.pickListTableRef.resetTotalsForEmptyList();
    },
    emptyListHandler() {
      this.emptyPickListFlagValue = true;
    },
    async onDeleteLeftClick() {
      const item = this.itemsGetter;
      const criteria = {
        ...this.criteriaState,
        workstation: Number(this.wkstNumber),
      };
      if ((item || {})._id) {
        const currentIndex = this.pickListItemsGetter.findIndex((ft) => ft._id
         === this.highlightedId(this.pickListBaseCrudKey));
        await this.deletePickList({ item, baseCrudKey: this.pickListBaseCrudKey }).then(() => {
          const nextItem = this.pickListItemsGetter[currentIndex];
          if ((nextItem || {})._id) {
            this.setItem([this.pickListBaseCrudKey, (nextItem || {})]);
            this.setHighlightedId([this.pickListBaseCrudKey, (nextItem._id || '')]);
          } else this.setItem([this.pickListBaseCrudKey, {}]);
        });

        this.$refs.pickListTableRef.getRecordCount(criteria);
      } else this.custumColrFlashMsg({ msg: 'Please select a record' });
    },

    handleAddressChange() {
      if (this.addEmailAddressOnly) this.setIsVisible(['pickListEmailAddressDialog', true]);
      else this.setIsVisible(['pickListAddressDialog', true]);
    },
    async getBusinnesDate() {
      const globalSettingVariables = await this.getGlobalSettingStore({});
      const todaysDate = toShortDate();
      let bussDate = todaysDate;
      const bussDateChangeIndividually = this.paymentSettingItem.changeBussDateIndividually;
      if (!bussDateChangeIndividually) {
        bussDate = (globalSettingVariables.configuration || {}).businessDate || todaysDate;
      } else {
        const localBussDate = getBusinessDateForPaymentEntry();
        bussDate = localBussDate || todaysDate;
      }
      return bussDate;
    },
    refetchTaxInquiryData() {
      this.$emit('refetchTaxInquiryData');
    },
    async handleMakePayment() {
      const pickListItems = this.pickListItemsGetter || [];
      if (pickListItems.length) {
        const bussDate = await this.getBusinnesDate();
        // fiscal year must follow the Business Date for payment entry (same as postprocessData),
        // so the backend seeds paymentNumber and returns FY for the correct fiscal year
        const fiscalYear = toFiscalYear(bussDate ? new Date(bussDate) : new Date(this.todaysDate));
        // update criteria for make payment dialog
        const criteria = {
          ...this.criteriaState,
          workstation: Number(this.wkstNumber),
          penaltyDate: this.editedItem.curPenaltyDate,
          fiscalYear,
        };
        this.setCriteria(['pickListMakePaymentDialog', criteria]);
        this.setIndex(['pickListMakePaymentDialog', -1]);
        //  need to call pick list payment api to get load on large payment list.
        this.setItems(['largePaymentList', []]);
        this.setItem(['pickListMakePaymentDialog', this.postprocessData([], bussDate)]);
        this.setIsVisible(['pickListMakePaymentDialog', true]);
      }
    },
    postprocessData(data, bussDate) {
      const taxDetails = [];
      const currentDate = this.editedItem.curPenaltyDate
        ? toShortDate(this.editedItem.curPenaltyDate) : toShortDate();
      const formatedDateForFiscalYear = bussDate
        ? new Date(bussDate) : new Date(this.todaysDate);
      const fiscalVal = toFiscalYear(formatedDateForFiscalYear);
      for (const item of data) {
        taxDetails.push({
          taxType: 'TAXPAYMENT',
          schoolDistrict: item.schoolDistrict,
          ownerName: item.ownerName,
          originalTaxId: item.taxId,
          taxYear: item.taxYear,
          taxId: item.taxId,
          isExempt: item.isExempt || false,
          isFromOtherSources: true,
          isFromProtestOrPicklist: true, // this will help in warning msg
          itsNewRecord: true, // this will hepl to get reciept num & in other functinalities
          taxAmt: item.exemptAmt,
        });
      }
      const paymentDetails = {
        paidBy: '',
        paymentType: '',
        amount: '0',
      };
      const items = {
        fiscalYear: fiscalVal,
        payDate: bussDate,
        workstation: Number(this.wkstNumber) || Number(getWorkstationNumber()) || null,
        taxDetails,
        paymentDetails: [paymentDetails],
        penaltyDate: currentDate,
      };

      return items;
    },
    urlEncodeObject(obj) {
      return new URLSearchParams(obj).toString();
    },
    getAuthToken() {
      return getRawJwt();
    },
    printList() {
      const penDate = this.editedItem.curPenaltyDate
        ? toShortDate(this.editedItem.curPenaltyDate) : toShortDate();

      const criteriaOfPickList = this.criteria(this.pickListBaseCrudKey);
      const sortCriteria = {
        sortBy: [
          { column: 'taxId', direction: 'asc' },
        ],
      };
      if (criteriaOfPickList.sortBy && criteriaOfPickList.sortBy) {
        sortCriteria.sortBy = criteriaOfPickList.sortBy;
      }

      const encodedCriteria = encodeURIComponent(JSON.stringify(sortCriteria));
      const params = [
        `token=${this.getAuthToken()}`,
        `filters=${encodedCriteria}`,
        `singleLineInfo=${this.singleLineInfo}`,
        `fullLegalDescription=${this.fullLegalDescription}`,
        `wkstID=${Number(this.wkstNumber)}`,
        `penaltyDate=${penDate}`,
        `profileName=${encodeURIComponent(this.criteriaState.profileName || '')}`,
      ];
      if (this.devSetting && this.devSetting.isTestEnvironment) {
        params.push('isTestCafe=1');
      }
      const queryString = generateQueryString(params);
      const to = `taxInquiry/pick-list/printlist${queryString}`;
      const url = `${backendRest.defaults.baseURL}/${to}`;
      printerHandler(this, { url, reportName: 'infoLists', deafultPrinterMode: true });
    },

    printAddressLabel() {
      const criteria = {
        token: this.getAuthToken(),
        workstation: this.wkstNumber,
        profileName: this.criteriaState.profileName,
      };
      const to = `taxInquiry/address-label?${this.urlEncodeObject(criteria)}`;
      const toUrl = `${backendRest.defaults.baseURL}/${to}`;
      printerHandler(this, { url: toUrl, reportName: 'addressLabels', deafultPrinterMode: true });
    },
    async penaltyCaclFunc(val) {
      const itemData = this.pickListItemsGetter;
      const formattedArray = [];
      clearTimeout(this.delayTimerId);
      this.delayTimerId = setTimeout(async () => {
        this.setHighlightedId([this.pickListBaseCrudKey, '']);
        const { penaltyDate, penaltyRate } = this.taxSettingItem;
        for (let i = 0; i < (itemData || []).length; i += 1) {
          let totalFees = itemData[i].totalFee;
          // to get fee based on effective date
          const feeLis = itemData[i].feeList || [];
          const isExemptManufacturing = (itemData[i].statusCode || [])
            .includes(exemptManufacturingStatusCode);
          totalFees = isExemptManufacturing ? 0 : taxFeeCheckWitheEfectiveDate(feeLis, val);
          const returnedVal = penaltyCalculation(
            val, itemData[i].penaltyStartDate, penaltyDate,
            itemData[i].totalTax, itemData[i].totalPaid,
            totalFees, penaltyRate, itemData[i].totalDue,
            itemData[i].taxYear, itemData[i].statusCode,
            this.taxSettingItem,
          );
          itemData[i].feesAndPen = amountFormat(itemData[i].totalDue
           + returnedVal.penaltyWithOrWithoutFees);
          itemData[i].getPenRateWithMonths = returnedVal.getPenRateWithMonths;
          itemData[i].penaltyDateValue = val;
          formattedArray.push(itemData[i]);
        }

        this.setItems(this.pickListBaseCrudKey, formattedArray);

        const pickListFil = this.criteriaState;
        const pickListCriteria = this.criteria(this.pickListBaseCrudKey);
        this.criteriaState.isItFromProfile = true;
        const criteria = {
          limit: (pickListCriteria.limit || 100),
        };
        if (pickListFil.profileName) {
          criteria.profileName = pickListFil.profileName;
          delete criteria.workstation;
        } else {
          criteria.workstation = Number(this.wkstNumber);
        }

        this.$refs.pickListTableRef.getRecordCount(criteria);
      }, SEARCH_DEBOUNCE_TIMER);
    },
    openConfirmPopUp(fromWhere) {
      const { profileName } = this.criteriaState;
      if (fromWhere === 'no') this.emptyPickListFlagValue = false;
      else if (fromWhere === 'yes') {
        if (profileName) this.onPickListDelete();
        else {
          this.emptyPickListFlagValue = false;
          const item = {
            workstation: Number(this.wkstNumber),
          };
          this.deleteAllPickList({ item, baseCrudKey: this.pickListBaseCrudKey });
        }
        this.$refs.pickListTableRef.resetTotalsForEmptyList();
      }
    },
    async printStatement() {
      if (!this.togglePrintOrEmail) {
        const items = this.items(this.pickListBaseCrudKey);
        if (items.length < 1) { alert('Please add at least one record'); return; }
        const { jwt } = await this.reAuth();
        const criteriaOfPickList = this.criteria(this.pickListBaseCrudKey);
        const sortCriteria = {
          sortBy: [
            { column: 'taxId', direction: 'asc' },
          ],
        };
        if (criteriaOfPickList.sortBy && criteriaOfPickList.sortBy) {
          sortCriteria.sortBy = criteriaOfPickList.sortBy;
        }

        const encodedCriteria = encodeURIComponent(JSON.stringify(sortCriteria));
        const penaltyDate = toIsoDate(this.editedItem.curPenaltyDate);

        const params = [
          `token=${jwt}`,
          `filters=${encodedCriteria}`,
          `penaltyDate=${penaltyDate}`,
          'status=picklist',
          `wkst=${Number(this.wkstNumber)}`,
          `profileName=${encodeURIComponent(this.criteriaState.profileName || '')}`,
          // TKT-000862: the Middle Section choice made on the search screen applies to pick
          // list statement prints too. Empty on County Setting and dropped by
          // generateQueryString, so those prints are unchanged.
          middleSectionParam(),
        ];
        if (this.criteriaState.profileName !== '' && this.criteriaState.profileName !== undefined) {
          params.push('allwkst=true');
        }
        const queryString = generateQueryString(params);
        const to = `taxInquiry/pick-list/printStatement${queryString}`;
        const url = `${backendRest.defaults.baseURL}/${to}`;
        if (this.taxSettingItem.addCommentOnStatementMailed) {
          this.pendingStatementUrl = url;
          this.showMailingDialog = true;
        } else {
          printerHandler(this, { url, reportName: 'taxStatements', deafultPrinterMode: true });
        }
      } else {
        // console.info('pick');
      }
    },
    confirmStatementMailed(isMailed) {
      this.showMailingDialog = false;
      if (!this.pendingStatementUrl) return;
      const url = isMailed
        ? `${this.pendingStatementUrl}&mailedStatement=1`
        : this.pendingStatementUrl;
      printerHandler(this, { url, reportName: 'taxStatements', deafultPrinterMode: true });
      this.pendingStatementUrl = null;
    },
    async handleTaxAmountBreakdown() {
      const records = this.items(this.pickListBaseCrudKey);
      // Calculate the total sum of the amounts
      const sumOfTotalDue = records.reduce((total, record) => total + (record.totalDue || 0), 0);
      const totalBudget = amountToNumber(this.exemptAmount);
      let runningTotal = 0;

      const updatedRecords = records.map((record) => {
        // Fix the proportion calculation - parentheses were in wrong place
        const proportion = (amountToNumber(record.totalDue) || 0) / sumOfTotalDue;
        const updateTaxDue = amountToNumber(totalBudget * proportion);
        runningTotal += updateTaxDue;
        return {
          ...record,
          updateTaxDue,
        };
      });

      // Handle decimal adjustment - apply to the last record's updateTaxDue
      const difference = totalBudget - runningTotal;
      if (difference !== 0 && updatedRecords.length > 0) {
        const lastRecord = updatedRecords[updatedRecords.length - 1];
        lastRecord.updateTaxDue += difference; // Adjust the last record
      }
      const updatedItem = [];
      updatedRecords.forEach((element) => {
        const item = element;
        delete item.totalPayment;
        delete item.amountStillDue;
        updatedItem.push({
          taxType: 'TAXPAYMENT',
          originalTaxId: item.taxId,
          taxYear: item.taxYear,
          taxId: item.taxId,
          exemptAmt: amountFormat(item.updateTaxDue),
          isExempt: true,
          isFromOtherSources: true,
          isFromProtestOrPicklist: true, // this will help in warning msg
          schoolDistrict: item.schoolDistrict,
          ownerName: `${item.name1 || ''} ${item.name2 || ''}`,
          itsNewRecord: true, // this will hepl to get reciept num & in other functinalities
          taxAmt: item.exemptAmt,
        });
      });
      const bussDate = await this.getBusinnesDate();
      // fiscal year must follow the Business Date for payment entry (same as postprocessData)
      const fiscalYear = toFiscalYear(bussDate ? new Date(bussDate) : new Date(this.todaysDate));
      delete this.criteriaState.statusCodes;
      const criteria = {
        ...this.criteriaState,
        workstation: Number(this.wkstNumber),
        penaltyDate: this.editedItem.curPenaltyDate,
        exemptAmount: totalBudget,
        enableExempt: this.enableExempt,
        fiscalYear,
      };
      this.setCriteria(['pickListMakePaymentDialog', criteria]);
      this.setItem(['pickListMakePaymentDialog', this.postprocessData(updatedItem, bussDate)]);
      this.setIsVisible(['pickListMakePaymentDialog', true]);
      // await this.upsertBulkPickList({
      //   item: updatedItem,
      //   baseCrudKey: 'pickListTable',
      // }).then(() => {
      //   this.loadPickListsForProtestTax();
      // });
    },
    dataFromCommonDropDowns(val, whichField) {
      // if mtg is selected, statuscode needs to be disabled and vise-versa
      if (whichField === 'mtgCode') {
        if (val) this.criteriaState.statusCode = null;
      }
      if (whichField === 'statusCode') {
        if (val) this.criteriaState.mtgCode = null;
      }
      this.criteriaState[whichField] = val;
    },
    addNewProfileName() {
      this.criteriaState.profileName = '';
      this.searchByCriteria();
    },
    async searchByCriteria() {
      const pickListFil = this.criteriaState;
      const pickListCriteria = this.criteria(this.pickListBaseCrudKey);
      this.criteriaState.isItFromProfile = true;
      const criteria = {
        limit: (pickListCriteria.limit || 100),
      };
      if (pickListFil.profileName) {
        criteria.profileName = pickListFil.profileName;
        delete criteria.workstation;
      } else {
        criteria.workstation = Number(this.wkstNumber);
      }
      this.loadPickLists({
        criteria,
        baseCrudKey: this.pickListBaseCrudKey,
        postProcessTransaction: this.taxPostProcessTrans,
      });
      localStorage.setItem('pick-list-profile-name', pickListFil.profileName || '');
      await this.$refs.pickListTableRef.getRecordCount(criteria);
    },
    async loadRecordsClicked() {
      this.loadRecordsDisable = true;
      this.criteriaState.profileName = '';
      this.criteriaState.isItFromProfile = false;
      const pickListFil = this.criteriaState;
      delete pickListFil.isItFromProfile;
      const pickListCriteria = this.criteria(this.pickListBaseCrudKey);
      const criteria = {
        workstation: Number(this.wkstNumber),
        limit: (pickListCriteria.limit || 100),
        ...pickListFil,
      };

      // if its exempt manufacturing, then we need to load all the records
      if (pickListFil && pickListFil.statusCode === exemptManufacturingStatusCode) {
        delete criteria.limit;
        delete criteria.skip;
      }
      if (pickListFil.statusCode || pickListFil.mtgCode || this.criteriaState.envelopeNumber) {
        await this.pickListLoadAndBuildRecords({
          criteria,
          baseCrudKey: this.pickListBaseCrudKey,
          postProcessTransaction: this.taxPostProcessTrans,
        });
        this.loadRecordsDisable = false;

        criteria.penaltyDate = this.editedItem.curPenaltyDate || null;
        // Get record count from PickListTable component
        await this.$refs.pickListTableRef.getRecordCount(criteria);
      } else {
        this.custumColrFlashMsg({ msg: 'Either mtg code or status code needs to be selected!' });
        this.loadRecordsDisable = false;
      }
    },
    async handlePickListBarCodeScan() {
      const barcodeNum = this.barcodeInput;
      if (barcodeNum && this.lastScannedBarcode === barcodeNum) {
        const barcodeStr = String(barcodeNum);
        if (barcodeStr.length === 11) {
          this.custumColrFlashMsg({ msg: 'Duplicate barcode scan ignored' });
          return;
        }
      }
      this.lastScannedBarcode = barcodeNum;
      clearTimeout(this.barcodeScanTimer);
      this.barcodeScanTimer = setTimeout(async () => {
        await this.processBarcodeScan(barcodeNum);
      }, 300);
    },
    async processBarcodeScan(barcodeNum) {
      if (!barcodeNum) return;
      const barcodeStr = String(barcodeNum);
      if (barcodeStr.length !== 11) return;
      const taxYear = parseInt(barcodeStr.slice(0, 4), 10);
      const taxId = parseInt(barcodeStr.slice(4), 10);
      try {
        const item = {
          browseByPaidByList: [{ taxId, taxYear }],
          workstation: Number(this.wkstNumber),
        };
        if (this.criteriaState.profileName) {
          item.profileName = this.criteriaState.profileName;
          delete item.workstation;
        }
        await this.UpsertBrowsePaidByPickListSingleNMultiplipleHybrid({
          item,
          baseCrudKey: this.pickListBaseCrudKey,
        });
        this.barcodeInput = null;
        this.lastScannedBarcode = null;
        this.$refs.pickListTableRef.getPickLists();
        this.$nextTick(() => { this.$refs.barcodeInput.focus(); });
      } catch (error) {
        this.$store.dispatch('flashError', 'Failed to add record from barcode scan', { root: true });
      }
    },
    async taxPostProcessTrans(items) {
      const returnData = penaltyCaclCrudListCommonFunc(
        this.editedItem.curPenaltyDate, items, this.taxSettingItem, [], 'fromPickList',
      );
      return returnData;
    },
    async onPickListDelete(fromWhere) {
      const { profileName } = this.criteriaState;
      if (profileName) {
        const getProfileName = localStorage.getItem('pick-list-profile-name');
        // if the profile name is the same as the one in the local storage, then remove it
        if (fromWhere === 'fromDeleteButton' && getProfileName === profileName) localStorage.removeItem('pick-list-profile-name');
        const item = { profileName };
        await this.deletePickListBasedOnProfileName(
          { item, baseCrudKey: this.pickListBaseCrudKey },
        );
        this.$refs.pickListTableRef.getPickLists();
      } else {
        this.custumColrFlashMsg({ msg: 'Please select a Profile Name' });
      }
    },
    async onPickListSave() {
      const { profileName } = this.criteriaState;
      const workstationNumber = this.wkstNumber;

      if (profileName) {
        const itemData = this.items(this.pickListBaseCrudKey);
        const listOfIds = (itemData || []).map((ft) => ft._id);
        const item = { listOfIds, profileName, workstation: Number(workstationNumber) || null };
        await this.upsertPickListProfileNames({ item });
        this.$refs.pickListTableRef.getPickLists();
        localStorage.setItem('pick-list-profile-name', profileName || '');
      } else {
        this.custumColrFlashMsg({ msg: 'Please select a Profile Name' });
      }
    },
    resetDefault() {
      this.criteriaState = {
        taxYear: 0,
        statusCode: null,
        mtgCode: null,
        profileName: '',
      };
    },
    handleEmailStatement() {
      const itemData = this.item(this.baseCrudKey);
      const penDate = this.editedItem.curPenaltyDate
        ? toShortDate(this.editedItem.curPenaltyDate) : toShortDate();
      // this.editEmailItemStatements.taxYear = itemData.taxYear;
      // this.editEmailItemStatements.taxId = itemData.taxId;
      this.editEmailItem.wkstID = this.wkstNumber;
      this.editEmailItem.penaltyDate = penDate;
      this.editEmailItemStatements.emailAdd = itemData.emailAddess;
      // Default ON when the record has no address on file yet, OFF when the field is
      // pre-filled with what's already stored - the user can always override before sending.
      this.editEmailItemStatements.saveEmailToRecord = !itemData.emailAddess;
      this.editEmailItemStatements.bccEmailAddress = this.statementSettingItem.bccEmailAddress;
      this.editEmailItemStatements.subjectLine = this.statementSettingItem.subjectLine;
      this.editEmailItemStatements.subjectBody = this.statementSettingItem.subjectBody;
      this.setIsVisible([this.emailStatements, true]);
    },
    handleEmailInfoList() {
      const itemData = this.item(this.baseCrudKey);
      const penDate = this.editedItem.curPenaltyDate
        ? toShortDate(this.editedItem.curPenaltyDate) : toShortDate();
      this.editEmailItem.singleLineInfo = this.singleLineInfo;
      this.editEmailItem.fullLegalDescription = this.fullLegalDescription;
      this.editEmailItem.wkstID = this.wkstNumber;
      this.editEmailItem.penaltyDate = penDate;
      this.editEmailItem.emailAdd = itemData.emailAddess;
      this.editEmailItem.bccEmailAddress = this.statementSettingItem.bccEmailAddress;
      this.editEmailItem.subjectLine = this.statementSettingItem.subjectLine;
      this.editEmailItem.subjectBody = this.statementSettingItem.subjectBody;
      this.setIsVisible([this.emailStatementCrudKeyPickList, true]);
    },
    async handleEmail() {
      // const itemData = this.item(this.baseCrudKey);
      const {
        bccEmailAddress, emailAdd, subjectBody, subjectLine,
        singleLineInfo, fullLegalDescription, wkstID, penaltyDate,
      } = this.editEmailItem;

      const item = {
        email: emailAdd,
        bccEmailAddress,
        subject: subjectLine,
        subjectBody,
        singleLineInfo,
        fullLegalDescription,
        wkstID,
        penaltyDate,
        profileName: this.criteriaState.profileName,
        token: this.getAuthToken(),
      };
      this.sendMailTR({ item });
    },
    async handleEmailStatements() {
      const {
        bccEmailAddress, emailAdd, subjectBody, subjectLine, saveEmailToRecord,
      } = this.editEmailItemStatements;

      // Always ensure penaltyDate is present and correct
      let penaltyDate = '';
      if (this.editedItem && this.editedItem.curPenaltyDate) {
        penaltyDate = toShortDate(this.editedItem.curPenaltyDate);
      } else if (this.criteriaState && this.criteriaState.penaltyDate) {
        penaltyDate = this.criteriaState.penaltyDate;
      } else {
        penaltyDate = toShortDate();
      }

      const item = {
        email: emailAdd,
        bccEmailAddress,
        subject: subjectLine,
        subjectBody,
        saveEmailToRecord: !!saveEmailToRecord,
        token: this.getAuthToken(),
        wkstID: this.wkstNumber,
        wkst: this.wkstNumber,
        status: 'picklist',
        penaltyDate,
      };
      // TKT-000862: emailed pick list statements follow the same workstation choice as the
      // printed ones; set only when overriding (the item is url-encoded whole).
      const middleSection = middleSectionValue();
      if (middleSection) item.middleSection = middleSection;
      this.sendMail({ item });
      // alert('handle email', item);
    },
    handleSMSDialog() {
      const rows = this.pickListItemsGetter || [];
      if (!rows.length) {
        this.custumColrFlashMsg({ msg: 'Please add at least one record to the pick list.' });
        return;
      }
      const first = rows[0];
      const phoneFromRow = first.primaryPhone
        || (first.taxInquiryTransaction && first.taxInquiryTransaction.primaryPhone)
        || '';
      this.editSMSItem.taxYear = first.taxYear;
      this.editSMSItem.taxId = first.taxId;
      this.editSMSItem.primaryPhone = phoneFromRow;
      this.editSMSItem.ownerName = `${first.name1 || ''} ${first.name2 || ''}`.trim();
      const globalCfg = (this.$store.state.SystemConfig && this.$store.state.SystemConfig.globalSettingItem) || {};
      const { baseUrlForSMS, countyNameForSMS } = resolveTaxSmsUrlParts(this.paymentSettingItem, globalCfg);
      if (!baseUrlForSMS || !countyNameForSMS) {
        this.custumColrFlashMsg({ msg: 'Please configure payment for generating SMS !' });
        return;
      }
      const items = rows.map((r) => ({ taxYear: r.taxYear, taxId: r.taxId }));
      this.editSMSItem.url = buildTaxSmsCartLinkUrl({
        baseUrlForSMS,
        countyNameForSMS,
        items,
      });
      this.editSMSItem.selectedCount = rows.length;
      this.setIsVisible([this.smsCrudKey, true]);
    },
    async handleMtgCodeUpsert() {
      // Get selected MTG code from the CRUD dialog
      const selectedMtgCode = this.criteriaState.mtgCode;
      // Get workstation number
      const workstationNumber = this.wkstNumber;
      if (!selectedMtgCode) {
        this.custumColrFlashMsg({ msg: 'Please select a Mortgage Code' });
        return;
      }
      // Get the pick list items to apply MTG code to
      const pickListItems = this.pickListItemsGetter || [];

      if (pickListItems.length === 0) {
        this.custumColrFlashMsg({ msg: 'No items in pick list to apply MTG code' });
        return;
      }
      const item = {
        mtgCode: selectedMtgCode,
        workstation: Number(workstationNumber),
        profileName: this.criteriaState.profileName,
      };
      await this.updateMtgToPickList({ item });
    },
  },
};
</script>
